import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiCheck,
  FiHeart,
  FiMinus,
  FiPlus,
  FiRefreshCw,
  FiTruck,
} from "react-icons/fi";
import productData from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";

const formatPrice = (price) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: siteConfig.currency,
  }).format(Number(price) || 0);

const ingredientDescriptions = {
  aloe: "Comforts the scalp and supplies lightweight, water-rich hydration.",
  quinoa: "A plant protein that helps hair feel stronger, smoother and more resilient.",
  camellia: "A lightweight seed oil that softens strands and supports luminous shine.",
  mango: "Natural fruit sugars help draw lasting moisture into dry, porous hair.",
  rice: "A versatile botanical used to support softness, body and a polished finish.",
  hibiscus: "A flower extract that lends slip, softness and a polished-looking finish.",
  coconut: "Nourishing lipids cushion stressed strands and improve manageability.",
  mint: "Aromatic leaf extract that leaves the scalp feeling fresh and balanced.",
  default:
    "A thoughtfully selected botanical that supports healthy-looking hair and skin.",
};

const getHowToUse = (category) => {
  if (category === "Shampoo") {
    return "Apply a quarter-sized amount to thoroughly wet hair. Massage into the scalp and work the lather through the lengths. Rinse well; repeat when roots need a deeper reset.";
  }
  if (category === "Conditioner") {
    return "After cleansing, distribute from mid-lengths to ends. Leave for two to three minutes, detangle gently, then rinse with cool water for added shine.";
  }
  if (category === "Treatment") {
    return "Use on clean, damp hair as the final care step. Begin with a small amount and layer only where your scalp or lengths need extra support.";
  }
  if (category === "Styling") {
    return "Work a small amount through damp hair, concentrating on the areas that need definition or control. Air-dry or diffuse on low heat, then reshape with your hands.";
  }
  if (category === "Body Care") {
    return "Massage over damp or dry skin using slow, circular motions. Layer as needed on areas that feel dry and allow the botanical formula to absorb.";
  }
  return "Use the products in order on wet hair, beginning with cleanse and following with care or treatment. Adjust the amount to your hair length and density.";
};

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const {
    addToCart,
    toggleWishlist,
    isWishlisted,
  } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || "Original"
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [imageStatus, setImageStatus] = useState("loading");

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size?.[0] || "Standard");
    setSelectedColor(product.colors?.[0] || "Original");
    setSelectedImage(0);
    setQuantity(1);
    setAdded(false);
    setImageStatus("loading");
  }, [product]);

  useEffect(() => {
    if (!product) return;
    document.title = `${product.name} · ${siteConfig.brandName}`;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    descriptionMeta?.setAttribute("content", product.subtitle);
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return productData
      .filter((item) => item.id !== product.id)
      .sort(
        (a, b) =>
          Number(b.category === product.category) -
            Number(a.category === product.category) ||
          Number(Boolean(b.bestSeller)) - Number(Boolean(a.bestSeller))
      )
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail__not-found">
        <span>404</span>
        <h1>That ritual could not be found.</h1>
        <p>The product may have moved, or the link may be incomplete.</p>
        <Link to="/shop">Return to the collection</Link>
      </main>
    );
  }

  const ingredients = String(product.attributes?.keyIngredients || "")
    .split(",")
    .map((ingredient) => ingredient.trim())
    .filter(Boolean);
  const favorite = isWishlisted(product.id);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2400);
  };

  const selectImage = (index) => {
    setSelectedImage(index);
    setImageStatus("loading");
  };

  return (
    <main className="product-detail">
      <nav className="product-breadcrumb" aria-label="Breadcrumb">
        <Link to="/" onClick={toTop}>Home</Link>
        <span aria-hidden="true">/</span>
        <Link to="/shop" onClick={toTop}>Shop</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <section className="product-detail__container" aria-labelledby="product-title">
        <div className="product-gallery">
          <div className="product-thumbnails" aria-label="Product images">
            {product.images.map((image, index) => (
              <button
                type="button"
                key={image}
                className={index === selectedImage ? "is-active" : ""}
                onClick={() => selectImage(index)}
                aria-label={`Show product image ${index + 1}`}
                aria-pressed={index === selectedImage}
              >
                <img src={image} alt="" />
              </button>
            ))}
          </div>

          <div className={`product-main-image is-${imageStatus}`}>
            {imageStatus === "loading" && (
              <span className="product-image-skeleton" aria-hidden="true" />
            )}
            {imageStatus === "error" ? (
              <div className="product-image-error" role="status">
                Image unavailable
              </div>
            ) : (
              <img
                src={product.images[selectedImage]}
                alt={`${product.name}, view ${selectedImage + 1}`}
                onLoad={() => setImageStatus("loaded")}
                onError={() => setImageStatus("error")}
              />
            )}
          </div>
        </div>

        <div className="product-info">
          <div className="product-rating" aria-label={`${product.rating} out of 5 stars from ${product.reviews} reviews`}>
            <span aria-hidden="true">★★★★★</span>
            <strong>{product.rating}</strong>
            <a href="#product-details">{product.reviews} reviews</a>
          </div>
          <p className="product-category">{product.category}</p>
          <h1 id="product-title">{product.name}</h1>
          <p className="product-subtitle">{product.subtitle}</p>
          <p className="product-price">{formatPrice(product.price)}</p>

          <div className="product-option">
            <div className="product-option__heading">
              <span>Size</span>
              <small>{selectedSize}</small>
            </div>
            <div className="product-size-options">
              {product.size.map((size) => (
                <button
                  type="button"
                  key={size}
                  className={selectedSize === size ? "is-selected" : ""}
                  onClick={() => setSelectedSize(size)}
                  aria-pressed={selectedSize === size}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {product.colors.length > 0 && (
            <label className="product-formula" htmlFor="product-formula">
              <span>Natural notes</span>
              <select
                id="product-formula"
                value={selectedColor}
                onChange={(event) => setSelectedColor(event.target.value)}
              >
                {product.colors.map((color) => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
            </label>
          )}

          <div className="purchase-choice">
            <div>
              <span className="purchase-choice__radio" aria-hidden="true" />
              <div>
                <strong>One-time purchase</strong>
                <small>Ships in recyclable, low-waste packaging.</small>
              </div>
            </div>
            <strong>{formatPrice(product.price)}</strong>
          </div>

          <div className="product-purchase-actions">
            <div className="product-quantity" aria-label="Quantity">
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                aria-label="Decrease quantity"
                disabled={quantity === 1}
              >
                <FiMinus aria-hidden="true" />
              </button>
              <span aria-live="polite">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.min(10, current + 1))}
                aria-label="Increase quantity"
                disabled={quantity === 10}
              >
                <FiPlus aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className={`product-add-button ${added ? "is-added" : ""}`}
              onClick={handleAddToCart}
            >
              {added ? <><FiCheck aria-hidden="true" /> Added to bag</> : "Add to bag"}
            </button>
            <button
              type="button"
              className={`product-wishlist ${favorite ? "is-active" : ""}`}
              onClick={() => toggleWishlist(product.id)}
              aria-label={favorite ? "Remove from wishlist" : "Add to wishlist"}
              aria-pressed={favorite}
            >
              <FiHeart aria-hidden="true" />
            </button>
          </div>

          <p className={`product-added-message ${added ? "is-visible" : ""}`} aria-live="polite">
            Your ritual is in the bag. <Link to="/cartpage">View bag</Link>
          </p>

          <div className="product-service-notes">
            <p><FiTruck aria-hidden="true" /> Tracked shipping {formatPrice(siteConfig.shippingFlatRate)}</p>
            <p><FiRefreshCw aria-hidden="true" /> Easy returns within {siteConfig.policies.returnWindow} days</p>
          </div>

          <div className="product-accordion" id="product-details">
            <details open>
              <summary>Product details</summary>
              <p>{product.description}</p>
              <dl>
                {Object.entries(product.attributes || {}).map(([label, value]) => (
                  <div key={label}>
                    <dt>{label.replace(/([A-Z])/g, " $1")}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </details>
            <details>
              <summary>How to use</summary>
              <p>{getHowToUse(product.category)}</p>
            </details>
            <details>
              <summary>Shipping & returns</summary>
              <p>
                Orders usually dispatch in {siteConfig.policies.dispatchTime}. See our <Link to="/shipping">shipping</Link> and <Link to="/returns">return</Link> policies for details.
              </p>
            </details>
          </div>
        </div>
      </section>

      <section className="product-use-section" aria-labelledby="how-to-use-title">
        <div className="product-use-section__image">
          <img src="/main/product-ritual.png" alt="Hands applying a lightweight botanical serum to healthy hair" loading="lazy" />
        </div>
        <div className="product-use-section__content">
          <span>Make it a ritual</span>
          <h2 id="how-to-use-title">How to use</h2>
          <p>{getHowToUse(product.category)}</p>
          <ol>
            <li><strong>Start small.</strong> Our concentrated formulas are designed to be layered.</li>
            <li><strong>Work with intention.</strong> Give the scalp and lengths time to absorb each step.</li>
            <li><strong>Adjust by texture.</strong> Fine hair needs less; dense curls may welcome more.</li>
          </ol>
        </div>
      </section>

      <section className="product-standards" aria-label="Our formulation standards">
        <div><strong>100%</strong><span>vegan formulas</span></div>
        <div><strong>0</strong><span>sulfates or parabens</span></div>
        <div><strong>25</strong><span>original botanical care rituals</span></div>
      </section>

      <section className="product-ingredients" aria-labelledby="ingredients-title">
        <div className="product-section-heading">
          <span>From source to shower</span>
          <h2 id="ingredients-title">Key ingredients</h2>
        </div>
        <div className="product-ingredient-grid">
          {ingredients.map((ingredient, index) => {
            const ingredientKey = ingredient.toLowerCase();
            const noteKey = Object.keys(ingredientDescriptions).find(
              (key) => key !== "default" && ingredientKey.includes(key)
            );
            return (
              <article key={ingredient}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{ingredient}</h3>
                <p>{ingredientDescriptions[noteKey] || ingredientDescriptions.default}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="product-related" aria-labelledby="related-title">
        <div className="product-section-heading">
          <span>Better together</span>
          <h2 id="related-title">Complete the ritual</h2>
        </div>
        <div className="product-related-grid">
          {relatedProducts.map((relatedProduct) => (
            <article key={relatedProduct.id}>
              <Link to={`/shopDetails/${relatedProduct.id}`} onClick={toTop}>
                <img src={relatedProduct.images[0]} alt={relatedProduct.name} loading="lazy" />
              </Link>
              <p aria-label={`${relatedProduct.rating} out of 5 stars`}><span aria-hidden="true">★★★★★</span> {relatedProduct.rating}</p>
              <Link to={`/shopDetails/${relatedProduct.id}`} onClick={toTop}>{relatedProduct.name}</Link>
              <small>{relatedProduct.subtitle}</small>
              <strong>{formatPrice(relatedProduct.price)}</strong>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
